import { useEffect } from "react";
import { accountAttributionQueryKey } from "@/api/account-attribution-query-key";
import { accountAttributionStore } from "@/data/account-attribution-store";
import { useAuth } from "@/auth/auth-context";
import { resolveAttributionIdentity, } from "@/components/block/lip-attribution";
/**
 * When effective collection members arrive, resolve attribution for every
 * participant once, merge into the Zustand map, and seed TanStack Query cache
 * so all blocks with matching `createdBy` share one entry (no per-block
 * network fan-out — the members list is already loaded).
 *
 * Three side-effects from the same primed list:
 *
 *   1. `byAccountId` map of `AttributionIdentity` (the canonical
 *      avatar/initial cache the lip / comment popover / rail read
 *      from).
 *   2. `byEmail` reverse map (lowercased-email → Account.id) so callers
 *      who only have the user's email — primarily the auth-context's
 *      JWT payload — can recover the matching internal accountId.
 *   3. `currentAccountId` — when the signed-in user's email appears in
 *      the loaded members, capture their accountId so surfaces that
 *      need to gate on "is this mine?" (the comment delete button)
 *      can answer reliably without a separate `/v1/account/me`
 *      endpoint.
 *
 * `collectionId` + `dataUpdatedAt` must match the lip's `useQuery` key so
 * `setQueryData` hits the same cache entries the cards subscribe to.
 */
export function usePrimeAccountAttributionFromMembers(members, queryClient, collectionId, dataUpdatedAt, enabled) {
    // Auth user's email is what lets us identify "us" in the members list.
    // Captured here so the effect re-runs when the signed-in user changes
    // (sign-out + sign-in as a different user) — without it, a stale
    // currentAccountId could survive the transition.
    const { user } = useAuth();
    const currentEmail = user?.email?.trim().toLowerCase();
    useEffect(() => {
        if (enabled !== true || collectionId === undefined || !members || members.length === 0) {
            return;
        }
        const identityEntries = {};
        const emailEntries = {};
        let resolvedCurrentAccountId;
        const seen = new Set();
        for (const m of members) {
            if (seen.has(m.accountId))
                continue;
            seen.add(m.accountId);
            identityEntries[m.accountId] = resolveAttributionIdentity(m.accountId, members);
            // Build the email→accountId reverse map. `email` is optional on
            // a member (e.g. invited-by-phone accounts), so we only index
            // members that carry one. Lowercased to match the normalized
            // form the consumer hook (`useCurrentAccountId`) compares
            // against the auth user's email.
            const memberEmail = m.email?.trim().toLowerCase();
            if (memberEmail) {
                emailEntries[memberEmail] = m.accountId;
                if (currentEmail && memberEmail === currentEmail) {
                    resolvedCurrentAccountId = m.accountId;
                }
            }
        }
        const store = accountAttributionStore.getState();
        store.mergeIdentities(identityEntries);
        store.mergeEmailToAccountId(emailEntries);
        if (resolvedCurrentAccountId !== undefined) {
            store.setCurrentAccountId(resolvedCurrentAccountId);
        }
        for (const [accountId, identity] of Object.entries(identityEntries)) {
            queryClient.setQueryData([...accountAttributionQueryKey(accountId), collectionId, dataUpdatedAt], identity);
        }
    }, [members, queryClient, collectionId, dataUpdatedAt, enabled, currentEmail]);
}
